import { useEffect, useMemo, useRef } from "react";
import { ArrowUpCircle } from "lucide-react";

const firstNames = [
  "James", "Mary", "Robert", "Patricia", "John", "Jennifer", "Michael", "Linda", "David", "Elizabeth",
  "William", "Barbara", "Richard", "Susan", "Joseph", "Jessica", "Thomas", "Sarah", "Charles", "Karen",
  "Christopher", "Lisa", "Daniel", "Nancy", "Matthew", "Betty", "Anthony", "Margaret", "Mark", "Sandra",
  "Donald", "Ashley", "Steven", "Kimberly", "Paul", "Emily", "Andrew", "Donna", "Joshua", "Michelle",
  "Kenneth", "Carol", "Kevin", "Amanda", "Brian", "Dorothy", "George", "Melissa", "Timothy", "Deborah",
  "Ronald", "Stephanie", "Edward", "Rebecca", "Jason", "Sharon", "Jeffrey", "Laura", "Ryan", "Cynthia",
  "Jacob", "Kathleen", "Gary", "Amy", "Nicholas", "Angela", "Eric", "Shirley", "Jonathan", "Anna",
  "Stephen", "Brenda", "Larry", "Pamela", "Justin", "Emma", "Scott", "Nicole", "Brandon", "Helen",
  "Benjamin", "Samantha", "Samuel", "Katherine", "Raymond", "Christine", "Gregory", "Debra", "Frank", "Rachel",
  "Alexander", "Carolyn", "Patrick", "Janet", "Jack", "Catherine", "Dennis", "Maria", "Jerry", "Heather",
  "Tyler", "Diane", "Aaron", "Ruth", "Jose", "Julie", "Adam", "Olivia", "Nathan", "Joyce",
  "Henry", "Virginia", "Douglas", "Victoria", "Zachary", "Kelly", "Peter", "Lauren", "Kyle", "Christina",
  "Noah", "Joan", "Ethan", "Evelyn", "Jeremy", "Judith", "Walter", "Megan", "Christian", "Andrea",
  "Keith", "Cheryl", "Roger", "Hannah", "Terry", "Jacqueline", "Harry", "Martha", "Sean", "Gloria",
  "Austin", "Teresa", "Carl", "Ann", "Arthur", "Sara", "Lawrence", "Madison", "Dylan", "Frances",
  "Jesse", "Kathryn", "Jordan", "Janice", "Bryan", "Jean", "Billy", "Abigail", "Bruce", "Alice",
  "Albert", "Judy", "Willie", "Sophia", "Gabriel", "Grace", "Logan", "Denise", "Alan", "Amber",
  "Juan", "Doris", "Wayne", "Marilyn", "Elijah", "Danielle", "Randy", "Beverly", "Roy", "Isabella",
  "Vincent", "Theresa", "Ralph", "Diana", "Eugene", "Natalie", "Russell", "Brittany", "Bobby", "Charlotte",
  "Mason", "Marie", "Philip", "Kayla", "Louis", "Alexis", "Liam", "Lori", "Isaiah", "Alyssa",
  "Oliver", "Ella", "Lucas", "Claire", "Hunter", "Lily", "Connor", "Zoe", "Aiden", "Hazel",
  "Miles", "Stella", "Sebastian", "Penelope", "Julian", "Layla", "Caleb", "Riley", "Owen", "Luna",
  "Luke", "Aria", "Carter", "Nora", "Adrian", "Ellie", "Leo", "Mila", "Lincoln", "Aurora",
  "Jaxon", "Chloe", "Roman", "Savannah", "Max", "Elena", "Kai", "Paisley", "Finn", "Skyler",
  "Cole", "Nova", "Asher", "Bella", "Cooper", "Violet", "Emmett", "Scarlett", "Ryder", "Ivy",
  "Blake", "Maya", "Theo", "Willow", "Nolan", "Ruby", "Chase", "Eva", "Bennett", "Naomi",
  "Easton", "Valentina", "Landon", "Quinn", "Camden", "Piper", "Harrison", "Sadie", "Brooks", "Jade",
  "Sawyer", "Aubrey", "Grant", "Autumn", "Micah", "Faith", "Parker", "Genesis", "Reed", "Serenity",
  "Beau", "Nevaeh", "Colton", "Clara", "Declan", "Athena", "Axel", "Emery", "Tucker", "Lydia",
  "Weston", "Rose", "Grayson", "Ariana", "Dominic", "Eliana", "Cruz", "Peyton", "Tanner", "Lucia",
];

const lastInits = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";

function generateWithdrawals() {
  const list = [];
  for (let i = 0; i < 300; i++) {
    const first = firstNames[i % firstNames.length];
    const lastInit = lastInits[Math.floor(Math.random() * lastInits.length)];
    const amount = Math.floor(Math.random() * (100000 - 1000 + 1)) + 1000;
    const minutesAgo = Math.floor(Math.random() * 1440);
    list.push({
      name: `${first} ${lastInit}.`,
      amount,
      time: minutesAgo < 60 ? `${minutesAgo}m ago` : `${Math.floor(minutesAgo / 60)}h ago`,
    });
  }
  return list;
}

const WithdrawalTicker = () => {
  const withdrawals = useMemo(() => generateWithdrawals(), []);
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    let animId: number;
    let pos = 0;
    const speed = 0.5;

    const animate = () => {
      pos += speed;
      if (pos >= el.scrollHeight / 2) pos = 0;
      el.scrollTop = pos;
      animId = requestAnimationFrame(animate);
    };
    animId = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(animId);
  }, []);

  const doubled = [...withdrawals, ...withdrawals];

  return (
    <section className="py-24">
      <div className="container mx-auto px-4">
        <h2 className="text-center font-display text-3xl font-bold gradient-gold-text md:text-4xl">
          Recent Withdrawals
        </h2>
        <p className="mt-4 text-center text-muted-foreground font-body text-lg">
          Real-time withdrawal activity from our investors.
        </p>

        <div
          ref={scrollRef}
          className="mx-auto mt-12 max-w-2xl h-[400px] overflow-hidden rounded-xl border border-gold-dark/30 bg-card"
        >
          <div className="divide-y divide-border">
            {doubled.map((w, i) => (
              <div key={i} className="flex items-center justify-between px-5 py-3">
                <div className="flex items-center gap-3">
                  <ArrowUpCircle className="h-4 w-4 text-green-400" />
                  <span className="text-sm font-body text-foreground">{w.name}</span>
                </div>
                <div className="flex items-center gap-4">
                  <span className="font-display text-sm font-semibold text-gold">
                    ${w.amount.toLocaleString()}
                  </span>
                  <span className="text-xs text-muted-foreground font-body">{w.time}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default WithdrawalTicker;
